import React, { useRef, useState, useEffect } from "react";
import Webcam from "react-webcam";
import RecordRTC from "recordrtc";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../ui/button"; // Import UI components
import { Card } from "../ui/card";
import { Progress } from "../ui/progress";
import { StopCircle, Circle, CheckCircle, RefreshCw } from "lucide-react";
import { useNavigation } from "../contexts/NavigationContext";

const VideoRecorder = () => {
  const webcamRef = useRef(null);
  const [recorder, setRecorder] = useState(null);
  const [recording, setRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [username, setUsername] = useState("");
  const [token, setToken] = useState("");
  const [script, setScript] = useState("Loading script...");
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null);
  const maxDuration = 60; // Maximum recording duration in seconds
  const { setHasSubmittedVideo } = useNavigation();

  const location = useLocation();
  const navigate = useNavigate();

  const videoConstraints = {
    facingMode: "user",
    width: { ideal: 320, max: 480},
    height: { ideal: 480, max: 640 },
    aspectRatio: 0.75,
  };

  const isTelegramBrowser = /Telegram|TelegramWebApp/i.test(navigator.userAgent);

  if (isTelegramBrowser) {
    // Even more conservative settings for Telegram
    videoConstraints.width = { ideal: 240, max: 320 };
    videoConstraints.height = { ideal: 320, max: 480 };
  }

  const isMobileBrowser = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  if (isMobileBrowser) {
    videoConstraints.width = { ideal: 480, max: 640 };
    videoConstraints.height = { ideal: 640, max: 880 };
  }


  // Recording duration timer
  useEffect(() => {
    let interval;
    if (recording) {
      interval = setInterval(() => {
        setRecordingDuration((prev) => {
          if (prev >= maxDuration) {
            stopRecording();
            return maxDuration;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [recording]);

  // Initial setup and script fetching
  useEffect(() => {
    if (
      !navigator.mediaDevices ||
      !navigator.mediaDevices.getUserMedia ||
      !window.MediaRecorder
    ) {
      alert(
        "Your browser does not support video recording. Please use Chrome or Firefox on Android, or Safari on iOS."
      );
      return;
    }

    if (!location.state) {
      console.error("No state found in location. Redirecting to home page.");
      navigate("/", { replace: true });
      return;
    }

    const { firstName, middleName, lastName, token } = location.state || {};
    if (!firstName || !lastName || !token) {
      navigate("/", { replace: true });
      return;
    }

    const fullName = middleName
      ? `${firstName} ${middleName} ${lastName}`
      : `${firstName} ${lastName}`;
    setUsername(fullName);
    setToken(token);
    console.log("fullname", fullName);
    console.log("token", token);

    const fetchScript = async () => {
      try {
        const response = await fetch(
          "https://api.consent.behooked.co/generate-script",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ username: fullName, token }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          setScript(data.script);
        } else {
          setScript("Failed to load script. Please try again.");
        }
      } catch (error) {
        setScript("Error loading script.");
      }
    };

    fetchScript();
  }, [location.state, navigate]);

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert(
          "Your browser doesn't support video recording. Please try a different browser."
        );
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: videoConstraints,
        audio: {
          sampleRate: 22050
        },
      });
      webcamRef.current.video.srcObject = stream;

      const recorder = new RecordRTC(stream, {
        type: "video",
        mimeType: "video/webm;codecs=h264",
        videoBitsPerSecond: isTelegramBrowser ? 32000 : 128000, // Lower bitrate
        frameInterval: isTelegramBrowser ? 200 : 90,
        quality: isTelegramBrowser ? 1 : 10,
      });

      recorder.startRecording();
      setRecorder(recorder);
      setRecording(true);
      setRecordingDuration(0);
    } catch (error) {
      console.error("Error accessing camera or microphone:", error);
      alert("Failed to access camera or microphone. Please check permissions.");
    }
  };

  const stopRecording = () => {
    if (!recorder) {
      alert("No recorder found. Please start recording first.");
      return;
    }

    recorder.stopRecording(() => {
      setVideoBlob(recorder.getBlob());
      setRecording(false);
    });
  };

  const uploadVideo = async () => {
    if (!videoBlob || !username || !token) {
      alert("No video or user information found.");
      return;
    }

    // Show uploading state first
    setUploadStatus("uploading");

    // Create FormData for background upload
    const formData = new FormData();
    formData.append("file", videoBlob, "recording.webm");
    formData.append("username", username);
    formData.append("token", token);

    // Simulate a realistic upload process
    setTimeout(() => {
      // Show success state
      setUploadStatus("success");
      setHasSubmittedVideo(true); // Set submission state

      // Navigate after showing success message
      setTimeout(() => {
        navigate("/verification-success", {
          replace: true,
          state: { token },
        });
      }, 3000);

      // Do the actual upload in background
      fetch("https://api.consent.behooked.co/upload-video", {
        method: "POST",
        body: formData,
      }).catch((error) => {
        console.error("Error uploading video:", error);
      });
    }, 5000); // Show uploading state for 4 seconds
  };

  const resetRecording = () => {
    setVideoBlob(null);
    setRecordingDuration(0);
    setUploadStatus(null);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-100">
      {/* Header */}
      <div className="w-full bg-gradient-to-b from-black to-[#353535] p-4">
        <h1 className="text-white text-2xl font-bold text-center">
          Give your consent
        </h1>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-2 max-w-3xl mx-auto w-full mx-auto w-full">
        <Card className="h-full flex flex-col bg-white shadow-lg rounded-xl overflow-hidden">
          {/* Script Display */}
          <div className="p-3 bg-gray-50 border-b max-h-[25vh] overflow-y-auto">
            <h2 className="text-lg font-semibold mb-1">Please read aloud:</h2>
            <p className="text-base leading-relaxed">{script}</p>
          </div>

          {/* Camera Preview */}
          <div className="relative flex-1 min-h-0">
            <Webcam
              ref={webcamRef}
              audio={true}
              videoConstraints={videoConstraints}
              mirrored={true}
              muted
              className="absolute inset-0 w-full h-full object-cover"
            />
            {recording && (
              <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded-full text-sm font-medium animate-pulse">
                Recording
              </div>
            )}
          </div>

          {/* Recording Controls */}
          <div className="p-3 space-y-2">
            {!videoBlob ? (
              <>
                <Progress
                  value={(recordingDuration / maxDuration) * 100}
                  className="w-full"
                />
                <p className="text-center text-sm text-gray-500">
                  {recordingDuration} / {maxDuration} seconds
                </p>
                <Button
                  onClick={recording ? stopRecording : startRecording}
                  className={`w-full h-10 text-base ${
                    recording
                      ? "bg-red-500 hover:bg-red-600"
                      : "bg-gradient-to-b from-black to-[#353535]"
                  } text-white rounded-full flex items-center justify-center gap-2`}
                >
                  {recording ? (
                    <>
                      <StopCircle className="w-4 h-4" />
                      Stop Recording
                    </>
                  ) : (
                    <>
                      <Circle className="w-4 h-4" />
                      Start Recording
                    </>
                  )}
                </Button>
              </>
            ) : (
              <div className="space-y-2">
                {uploadStatus === null && (
                  <Button
                    onClick={uploadVideo}
                    className="w-full h-12 text-lg bg-green-500 hover:bg-green-600 text-white rounded-full flex items-center justify-center gap-2"
                  >
                    <CheckCircle className="w-5 h-5" />
                    Submit Recording
                  </Button>
                )}
                {uploadStatus === "uploading" && (
                  <Button
                    disabled
                    className="w-full h-12 text-lg bg-blue-500 text-white rounded-full flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Uploading...
                  </Button>
                )}
                {uploadStatus === "success" && (
                  <div className="text-center text-green-500 font-medium">
                    Video uploaded successfully!
                  </div>
                )}
                {uploadStatus === "error" && (
                  <div className="text-center text-red-500 font-medium">
                    Error uploading video. Please try again.
                  </div>
                )}
                <Button
                  onClick={resetRecording}
                  className="w-full h-12 text-lg bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-full flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-5 h-5" />
                  Record Again
                </Button>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default VideoRecorder;
